import moment from 'moment';

const toMomentDate = (dateValue) => {
  if (!dateValue) return null;

  const value = dateValue.iso || dateValue;

  if (moment.isMoment(value)) {
    return value.clone();
  }

  if (value instanceof Date) {
    return moment(value);
  }

  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return moment(value.toDate());
  }

  const isoDate = moment(value, moment.ISO_8601, true);
  if (isoDate.isValid()) {
    return isoDate;
  }

  const parsedDate = moment(value);
  return parsedDate.isValid() ? parsedDate : null;
};

/**
 * Calculate distance between two points in kilometers
 * @param {Object} point1 - First point with latitude and longitude
 * @param {Object} point2 - Second point with latitude and longitude
 * @returns {number} - Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
  const R = 6371; // Radius of the Earth in km
  const dLat = (point2.latitude - point1.latitude) * Math.PI / 180;
  const dLon = (point2.longitude - point1.longitude) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(point1.latitude * Math.PI / 180) * Math.cos(point2.latitude * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Filter quotes based on multiple criteria
 */
export const filterQuotes = (
  quotes,
  statusFilter,
  useStatusFilter,
  dateRange,
  useDateRange,
  subtotalMin,
  useSubtotalFilter,
  quoteCoords,
  useProximityFilter,
  proximityRange,
  userLocation,
  useCustomLocation,
  customLocation
) => {
  // First filter quotes based on criteria
  const filteredQuotes = quotes.filter(quote => {
    // Status filter
    if (statusFilter !== 'tutti' && quote.status !== statusFilter) {
      return false;
    }
    
    // Date range filter
    if (useDateRange && dateRange && dateRange.length === 2) {
      const quoteDate = toMomentDate(quote.date);
      const startDate = toMomentDate(dateRange[0])?.startOf('day');
      const endDate = toMomentDate(dateRange[1])?.endOf('day');

      if (!quoteDate || !startDate || !endDate) {
        return false;
      }
      
      if (!quoteDate.isBetween(startDate, endDate, null, '[]')) {
        return false;
      }
    }
    
    // Subtotal minimum filter
    if (useSubtotalFilter && quote.subtotal < subtotalMin) {
      return false;
    }
    
    return true;
  });
  
  // If proximity filter enabled, filter based on location
  if (useProximityFilter) {
    return filteredQuotes.filter(quote => {
      // Find coordinate for this quote's customer
      const quoteCoord = quoteCoords.find(
        coord => coord.cliente_pointer?.objectId === quote.customer_pointer?.objectId
      );
      
      // Skip if no coordinates
      if (!quoteCoord) return false;
      
      const center = useCustomLocation && customLocation 
        ? { latitude: customLocation.lat, longitude: customLocation.lng }
        : userLocation;
      
      if (center) {
        const distance = calculateDistance(
          { latitude: quoteCoord.latitude, longitude: quoteCoord.longitude },
          center
        );
        
        if (distance > proximityRange) {
          return false;
        }
      }
      
      return true;
    });
  }
  
  return filteredQuotes;
};

/**
 * Calculate statistics for quotes
 */
export const calculateQuoteStats = (quotes) => {
  if (!quotes || quotes.length === 0) {
    return {
      all: 0,
      accettato: 0, 
      bozza: 0,
      inviato: 0,
      pagato: 0,
      rifiutato: 0,
      totalAmount: 0
    };
  }
  
  const stats = {
    all: quotes.length,
    accettato: 0, 
    bozza: 0,
    inviato: 0,
    pagato: 0,
    rifiutato: 0,
    totalAmount: 0
  };
  
  quotes.forEach(quote => {
    // Count by status
    if (quote.status) {
      stats[quote.status] = (stats[quote.status] || 0) + 1;
    }
    
    // Sum total amount
    if (quote.subtotal) {
      stats.totalAmount += parseFloat(quote.subtotal);
    }
  });
  
  return stats;
};
